import type { CSSProperties, KeyboardEvent } from 'react';
import { Pill } from '@frm/ui';
import type { FrmTone } from '@frm/ui';
import { STATUS_LABEL } from '@/lib/status';
import type { AppEntry } from '@/types/app';

interface Props {
  app: AppEntry;
  onOpen: (app: AppEntry) => void;
}

// launcher-локальный маппинг status → пакетный tone.
// Сохраняем визуальную семантику: live=success, beta=danger как
// «осторожно, нестабильно» (не warning), soon=neutral.
const STATUS_TONE: Record<AppEntry['status'], FrmTone> = {
  live: 'success',
  beta: 'danger',
  soon: 'neutral',
};

/**
 * F1: единственный носитель «свежести» — порядок в Recent Strip
 * (этот компонент его НЕ дублирует бейджем "Недавнее").
 *
 * F10: для status=live в pill встроена анимированная точка `.live-dot`.
 */
export default function AppCard({ app, onOpen }: Props) {
  const isDisabled = app.status === 'soon' || !app.url;

  const handleClick = () => {
    if (isDisabled) return;
    onOpen(app);
  };
  const handleKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  };

  const style = { '--app-accent': app.accent } as CSSProperties;

  return (
    <div
      className="app-card"
      data-status={app.status}
      style={style}
      role="link"
      tabIndex={isDisabled ? -1 : 0}
      aria-disabled={isDisabled || undefined}
      onClick={handleClick}
      onKeyDown={handleKey}
    >
      <div className="app-card__icon" aria-hidden="true">
        {app.icon}
      </div>
      <h3 className="app-card__name">{app.name}</h3>
      <p className="app-card__desc">{app.description}</p>
      <div className="app-card__meta">
        <Pill
          tone={STATUS_TONE[app.status]}
          size="sm"
          leftIcon={
            app.status === 'live'
              ? <span className="live-dot" aria-hidden="true" />
              : undefined
          }
        >
          {STATUS_LABEL[app.status]}
        </Pill>
        {app.tags.slice(0, 2).map((t) => (
          <span key={t} className="app-card__tag">{t}</span>
        ))}
      </div>
      {/* F21: overlay «Скоро» появляется при hover/focus на disabled-карточке —
          даёт явную обратную связь, почему карточка не реагирует. Решение
          opacity 0.78 без overlay было неоднозначным: пользователь видел
          приглушение, но не понимал, что это «soon», а не «деградация». */}
      {app.status === 'soon' && (
        <div className="app-card__soon-overlay" aria-hidden="true">
          <span className="app-card__soon-label">Скоро</span>
        </div>
      )}
    </div>
  );
}
